"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import Title from "@/components/title";

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  items: TOCItem[]; // Pre-computed server-side
  config: {
    style: "bulleted" | "numbered" | "minimal";
  };
  className?: string;
}

export default function TableOfContents({
  items,
  config,
  className,
}: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("");

  // Handle scroll spy for active heading
  useEffect(() => {
    const handleScroll = () => {
      const headings = items
        .map((item) => document.getElementById(item.id))
        .filter(Boolean);
      const scrollPosition = window.scrollY + 100;

      // Find active heading
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        if (heading && heading.offsetTop <= scrollPosition) {
          setActiveId(heading.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial call

    return () => window.removeEventListener("scroll", handleScroll);
  }, [items]);

  if (!config.style || items.length === 0) {
    return null;
  }

  const getListStyle = () => {
    switch (config.style) {
      case "numbered":
        return "list-decimal";
      case "bulleted":
        return "list-disc";
      case "minimal":
      default:
        return "list-none";
    }
  };

  const getIndentClass = (level: number) => {
    switch (level) {
      case 1:
        return "ml-0";
      case 2:
        return "ml-4";
      case 3:
        return "ml-8";
      default:
        return "ml-0";
    }
  };

  return (
    <div className={cn("border-y border-line-strong py-5", className)}>
      <Title level={2} size="lg" weight="bold" className="mb-3 text-accent">
        In this article
      </Title>

      <nav aria-label="Table of contents">
        <ul
          className={cn(
            "space-y-2",
            config.style !== "minimal" && getListStyle(),
          )}
        >
          {items.map((item, index) => (
            <li key={item.id} className={cn(getIndentClass(item.level))}>
              <a
                href={`#${item.id}`}
                className={cn(
                  "block text-left w-full py-1 text-sm leading-6 transition-colors duration-200 hover:text-accent",
                  activeId === item.id
                    ? "text-accent font-semibold"
                    : "text-theme-light/80 hover:text-theme-light",
                )}
                aria-current={activeId === item.id ? "true" : undefined}
              >
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
